import Expression from '@/helpers/expressions/ThisExpression';
import BracketsCurly from '@/components/Icons/BracketsCurly';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { useState } from 'react';
import { IBM_Plex_Mono as ibmPlexMono } from 'next/font/google';
import Button from '../DesignLibrary/Button';
import Modal from '../Modal';
import ComplexEditor from './ComplexEditor';
import SimpleEditor from './SimpleEditor';
import { isSimpleExpression } from './utils';
import s from './ExpressionEditor.module.scss';

const mono = ibmPlexMono({
  subsets: ['latin'],
  weight: '400',
});

type FieldInfo = {
  key: string;
  label: string;
  type: string;
  options?: { key: string; label: string }[];
};

type ExpressionEditorProps = {
  fieldsInfo: Record<string, FieldInfo>;
  expression: Expression | null;
  onChange?: (expression: Expression) => void;
  canEditVars?: boolean;
  onlyComplexEditor?: boolean;
  canEditFilterExpressionProperties?: boolean;
};

const DEFAULT_EXPRESSION: Expression = {
  expression: '',
  filterExpression: null,
  filterExpressionItemSelector: null,
  filterExpressionOperand: null,
  filterExpressionReducer: null,
  filterExpressionVariableName: null,
  variables: [],
};

const ExpressionEditor: React.FC<ExpressionEditorProps> = ({
  expression,
  onChange,
  fieldsInfo,
  canEditVars,
  canEditFilterExpressionProperties,
  onlyComplexEditor,
}) => {
  const [showComplexEditor, setShowComplexEditor] = useState(false);

  const onlyComplexEditorAvailable = onlyComplexEditor || (expression && !isSimpleExpression(expression.expression));

  return (
    <div className={s.wrapper}>
      {onlyComplexEditorAvailable
        ? (
          <>
            {expression?.expression && <SyntaxHighlighter
              language="javascript"
              customStyle={{
                backgroundColor: 'var(--color-gray-15)',
                border: '1px solid var(--color-gray-25)',
                borderRadius: 4,
                fontSize: 11,
                margin: 0,
                padding: '6px 8px',
              }}
              wrapLongLines
              codeTagProps={{ style: mono.style }}
            >
              {expression.expression}
            </SyntaxHighlighter>}
          </>
        )
        : (
          <SimpleEditor
            expression={expression}
            onChange={onChange}
            fieldsInfo={fieldsInfo}
          />
        )}
      {showComplexEditor && (
        <Modal hasUnsavedChanges title="Expression Editor" onClose={() => setShowComplexEditor(false)}>
          <div className={s.conditionEditor} style={{ minHeight: 'unset' }}>
            <ComplexEditor
              canEditVars={canEditVars}
              expression={expression || DEFAULT_EXPRESSION}
              onChange={onChange}
              fieldsInfo={fieldsInfo}
              onSave={() => setShowComplexEditor(false)}
              canEditFilterExpressionProperties={canEditFilterExpressionProperties}
            />
          </div>
        </Modal>
      )}
      <div className={s.buttons}>
        <Button variant="secondary" size="sm" onClick={() => setShowComplexEditor(true)}>
          <BracketsCurly size={12} style={{ marginRight: 4 }} /> Expression Editor
        </Button>
      </div>
    </div>
  );
};

export default ExpressionEditor;
